import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {RequestV2StatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {
  RED_DARK,
  BLUE_DARKER,
  BLUE_POWDER,
  GREEN_LIGHTER,
  NEAR_BLACK,
  RED_LIGHTER,
  GRAY_LIGHTER,
} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface RequestV2StatusDisplayProps {
  status: RequestV2StatusType;
  dataTestAttrs?: DataTestAttrs;
}

export const REQUEST_V2_STATUS_STYLE_INFO: Record<
  RequestV2StatusType,
  {icon?: BenchlingIconName; name: string; color: string; backgroundColor: string}
> = {
  [RequestV2StatusType.DRAFT]: {
    icon: 'request-pending',
    name: 'Draft',
    color: NEAR_BLACK,
    backgroundColor: GRAY_LIGHTER,
  },
  [RequestV2StatusType.SENT]: {
    icon: 'request-in-progress',
    name: 'Sent',
    color: BLUE_DARKER,
    backgroundColor: BLUE_POWDER,
  },
  [RequestV2StatusType.DISCARDED]: {
    name: 'Discarded',
    color: NEAR_BLACK,
    backgroundColor: GRAY_LIGHTER,
  },
  [RequestV2StatusType.CANCELLED]: {
    icon: 'cancelled',
    name: 'Cancelled',
    color: RED_DARK,
    backgroundColor: RED_LIGHTER,
  },
  [RequestV2StatusType.FULFILLED]: {
    icon: 'request-executed',
    name: 'Fulfilled',
    color: NEAR_BLACK,
    backgroundColor: GREEN_LIGHTER,
  },
};

interface RequestV2StatusIconProps {
  type: RequestV2StatusType;
  overrideColor?: string;
}

export function RequestV2StatusIcon(props: RequestV2StatusIconProps): JSX.Element | null {
  const icon = REQUEST_V2_STATUS_STYLE_INFO[props.type]?.icon;
  if (icon == null) {
    return null;
  }
  const style: object | null = REQUEST_V2_STATUS_ICON_STYLES[props.type] ?? null;

  let styleOverrides = null;
  if (props.overrideColor) {
    styleOverrides = StyleSheet.create({
      overrideColor: {
        color: props.overrideColor,
      },
    });
  }

  return (
    <BenchlingIcon
      icon={icon}
      className={classnames(css(styles.requestStatusIcon), css(style, styleOverrides?.overrideColor))}
    />
  );
}

export default function RequestV2StatusDisplay(props: RequestV2StatusDisplayProps): JSX.Element {
  return (
    <span
      className={css(styles.requestStatusDisplayStyle, REQUEST_V2_STATUS_DISPLAY_STYLES[props.status])}
      {...getTestProps(props.dataTestAttrs)}
    >
      <RequestV2StatusIcon type={props.status} /> {REQUEST_V2_STATUS_STYLE_INFO[props.status].name}
    </span>
  );
}

const REQUEST_V2_STATUS_DISPLAY_STYLES = StyleSheet.create(
  _.mapObject(REQUEST_V2_STATUS_STYLE_INFO, (info) => ({
    color: info.color,
    backgroundColor: info.backgroundColor,
  }))
);

const REQUEST_V2_STATUS_ICON_STYLES = StyleSheet.create(
  _.mapObject(REQUEST_V2_STATUS_STYLE_INFO, (info) => ({color: info.color, marginRight: '6px'}))
);

const styles = StyleSheet.create({
  requestStatusIcon: {
    height: 12,
    width: 12,
  },
  requestStatusDisplayStyle: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingTop: 2,
    paddingBottom: 2,
    paddingRight: 8,
    paddingLeft: 8,
    borderRadius: 4,
  },
});
